import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { User } from '../../models/UserModel';
import useUsersService from '../../services/useUsersService';
import useAuthService from '../../services/authService';
import { getSessionUser, setSessionUser } from '../../services/authSession';
import { KeyRound, LogOut, User as UserIcon, ChevronDown } from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';
import LogoutConfirmModal from './LogoutConfirmModal';
import { userRoleDisplayLabel } from '../../utils/userRoleDisplay';

interface UserStatusProps {
  isCollapsed?: boolean;
}

const UserStatus = ({ isCollapsed = false }: UserStatusProps) => {
  const { me } = useUsersService();
  const { logout } = useAuthService();
  const [user, setUser] = useState<User | null>(null);
  const [isOnline, setIsOnline] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const collapsedPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cached = getSessionUser<User>();
    if (cached) setUser(cached);

    me({
      onSuccess: (resp: any) => {
        const data = resp?.data?.user;
        if (data) {
          setUser(data);
          setSessionUser(data);
          setIsOnline(true);
        }
      },
      onError: () => {
        setIsOnline(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapperRef.current?.contains(t)) return;
      if (isCollapsed && collapsedPanelRef.current?.contains(t)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [dropdownOpen, isCollapsed]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dropdownOpen]);

  const getDisplayName = () => {
    if (!user) return 'Usuario';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.username || user.email || 'Usuario';
  };

  const getInitials = () => {
    if (!user) return 'U';
    const f = (user.first_name || '').trim().charAt(0).toUpperCase();
    const l = (user.last_name || '').trim().charAt(0).toUpperCase();
    if (f && l) return `${f}${l}`;
    if (user.username) return user.username.charAt(0).toUpperCase();
    if (user.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  const getRoleLabel = () => userRoleDisplayLabel(user?.role);

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    setDropdownOpen(false);
    await logout();
  };

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  const renderMenuPanel = () => (
    <div className="user-status-dropdown__panel" onClick={(e) => e.stopPropagation()}>
      <div className="user-status-dropdown__header">
        <UserIcon size={18} />
        <span>Mi perfil</span>
      </div>
      <div className="user-status-dropdown__card">
        <div className="user-status-dropdown__card-avatar">{getInitials()}</div>
        <div className="user-status-dropdown__card-info">
          <div className="user-status-dropdown__card-name">{getDisplayName()}</div>
          {user?.email && <div className="user-status-dropdown__card-email">{user.email}</div>}
          <div className="user-status-dropdown__card-role">{getRoleLabel()}</div>
        </div>
      </div>
      <div className="user-status-dropdown__actions">
        <button
          type="button"
          className="user-status-dropdown__action"
          onClick={() => {
            setShowChangePasswordModal(true);
            setDropdownOpen(false);
          }}
        >
          <KeyRound size={18} />
          <span>Actualizar contraseña</span>
        </button>
        <button
          type="button"
          className="user-status-dropdown__action user-status-dropdown__action--logout"
          onClick={() => {
            setShowLogoutModal(true);
            setDropdownOpen(false);
          }}
        >
          <LogOut size={18} />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </div>
  );

  const collapsedMenuPortal =
    dropdownOpen &&
    isCollapsed &&
    typeof document !== 'undefined' &&
    createPortal(
      <>
        <div
          className="user-status-collapsed-backdrop"
          aria-hidden
          onClick={closeDropdown}
        />
        <div
          ref={collapsedPanelRef}
          className="user-status-dropdown user-status-dropdown--collapsed-portal"
          role="dialog"
          aria-label="Menú de perfil"
        >
          {renderMenuPanel()}
        </div>
      </>,
      document.body
    );

  return (
    <>
      <div className="user-status-wrapper" ref={wrapperRef}>
        <button
          type="button"
          className={`user-status-modern ${dropdownOpen ? 'user-status-modern--open' : ''} ${isCollapsed ? 'user-status-modern--collapsed' : ''}`}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          title={
            isCollapsed
              ? 'Cuenta, contraseña y cerrar sesión'
              : isOnline
                ? 'En línea'
                : 'Desconectado'
          }
          aria-label={`Abrir menú de cuenta: ${getDisplayName()}`}
          aria-expanded={dropdownOpen}
          aria-haspopup="true"
        >
          <div className="user-status-modern__avatar">
            <span className="user-status-modern__initials">{getInitials()}</span>
            <span className={`user-status-modern__dot ${isOnline ? 'online' : 'offline'}`} />
          </div>
          <div className="user-status-modern__info">
            <span className="user-status-modern__name">{getDisplayName()}</span>
            <span className={`user-status-modern__status ${isOnline ? 'online' : 'offline'}`}>
              {isOnline ? 'En línea' : 'Desconectado'}
            </span>
          </div>
          <ChevronDown
            className={`user-status-modern__chevron ${dropdownOpen ? 'open' : ''}`}
            size={18}
          />
        </button>

        {dropdownOpen && !isCollapsed && (
          <div
            className="user-status-dropdown"
            onClick={closeDropdown}
            role="dialog"
            aria-label="Menú de perfil"
          >
            {renderMenuPanel()}
          </div>
        )}
      </div>

      {collapsedMenuPortal}

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={() => setShowLogoutModal(false)}
      />
    </>
  );
};

export default UserStatus;
